/**
 * Phase 16.0 — Track Actions Menu (kebab dots ⋯)
 *
 * Compact owner/admin action sheet for a track. Shows up on every place
 * a track is rendered (profile rows, track detail page collapsed mobile
 * row, etc.) so creators can manage their own tracks without hunting
 * for the action.
 *
 * Available actions (rendered conditionally based on auth/ownership):
 *   • Edit Track  (owner only)
 *   • Add to Playlist (logged in)
 *   • Share to Community (logged in)
 *   • Share via system sheet / copy link (everyone)
 *   • Pin / Unpin (owner)
 *   • Delete (owner)
 *
 * Closes on outside click + ESC.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShareVisibility } from "@/lib/useShareVisibility";

const Item = ({ icon, label, onClick, to, testId, danger = false, disabled = false }) => {
  const cls = `w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
    disabled
      ? "text-gray-600 cursor-not-allowed"
      : danger
      ? "text-rose-200 hover:bg-rose-500/15"
      : "text-gray-200 hover:bg-purple-500/15"
  }`;
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        <span className="w-4 text-center">{icon}</span>
        <span>{label}</span>
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>
      <span className="w-4 text-center">{icon}</span>
      <span>{label}</span>
    </button>
  );
};

export const TrackActionsMenu = ({
  track,
  currentUser,
  isOwner = false,
  isAdmin = false,
  onPinToggle,
  onDelete,
  onShareCommunity,
  shareBaseUrl,
  testIdPrefix = "track-actions",
  align = "right",
}) => {
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
  const [playlistsError, setPlaylistsError] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const wrapRef = useRef(null);
  // Feb 2026 — CEO share-visibility gate. When OFF, both Share actions
  // (system share/copy + Share to Community) are hidden.
  const { canShare } = useShareVisibility();
  const tracksShareOn = canShare("tracks");

  // Outside click + ESC close.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Lazy-load the user's playlists the first time the menu opens.
  // Phase 21.3a5 fix — the previous version guarded setLoadingPlaylists(false)
  // behind a `cancelled` flag. If the user closed the menu before the fetch
  // resolved, `loadingPlaylists` was stuck true forever → the menu displayed
  // "Loading…" on every subsequent open. We now:
  //   1) ALWAYS reset loadingPlaylists in finally,
  //   2) track playlistsLoaded so the empty state renders correctly,
  //   3) surface a real Retry action if the fetch fails.
  useEffect(() => {
    if (!open || !currentUser) return undefined;
    if (playlistsLoaded || loadingPlaylists) return undefined;
    let cancelled = false;
    setLoadingPlaylists(true);
    setPlaylistsError(null);
    (async () => {
      try {
        const r = await api.get("/playlists/my");
        if (!cancelled) {
          setPlaylists(r.data?.playlists || []);
          setPlaylistsLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setPlaylists([]);
          setPlaylistsError(
            e?.response?.data?.detail || e?.message || "Couldn't load playlists"
          );
          setPlaylistsLoaded(true);
        }
      } finally {
        // Always release the loading lock — even if the effect was cancelled
        // (menu closed before fetch resolved). Otherwise the menu is stuck
        // on "Loading…" forever on the next open.
        setLoadingPlaylists(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, currentUser, playlistsLoaded, loadingPlaylists]);

  // Manual retry — clears the loaded flag so the effect re-runs.
  const retryLoadPlaylists = () => {
    setPlaylistsLoaded(false);
    setPlaylistsError(null);
  };

  const sid = track?.submission_id;

  const handleAddToPlaylist = async (playlistId) => {
    if (addingTo) return;
    setAddingTo(playlistId);
    try {
      await api.post(`/playlists/${playlistId}/tracks/${sid}`);
      toast.success("Added to playlist");
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't add to playlist");
    } finally {
      setAddingTo(null);
    }
  };

  const handleShare = async () => {
    const base = shareBaseUrl || (process.env.REACT_APP_BACKEND_URL || "");
    let shareUrl = `${base}/api/share/track/${sid}`;
    if (currentUser?.referral_code) {
      shareUrl += `?ref=${encodeURIComponent(currentUser.referral_code)}`;
    }
    api.post(`/track/${sid}/share`).catch(() => {});
    if (navigator.share) {
      try {
        await navigator.share({ title: track.track_title || "Track", url: shareUrl });
        setOpen(false);
        return;
      } catch {
        /* fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste anywhere");
    } catch {
      toast.error("Couldn't copy link");
    }
    setOpen(false);
  };

  const canManage = isOwner || isAdmin;

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testIdPrefix}-${sid}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o); }}
        className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Track actions"
        title="Track actions"
        data-testid={`${testIdPrefix}-trigger-${sid}`}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 ${align === "left" ? "left-0" : "right-0"} w-56 bg-[#15151c] border border-white/10 rounded-lg shadow-2xl py-1`}
          role="menu"
          data-testid={`${testIdPrefix}-menu-${sid}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Edit Track — owner / admin */}
          {canManage && (
            <Item
              icon="✏️"
              label="Edit Track"
              to={`/track/${sid}/edit`}
              testId={`${testIdPrefix}-edit-${sid}`}
            />
          )}

          {/* Add to Playlist — logged in */}
          {currentUser && (
            <div className="border-t border-white/5">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500">Add to playlist</div>
              {loadingPlaylists ? (
                <div
                  className="px-3 py-2 text-xs text-gray-500"
                  data-testid={`${testIdPrefix}-playlists-loading-${sid}`}
                >
                  Loading…
                </div>
              ) : playlistsError ? (
                <div
                  className="px-3 py-2 text-xs"
                  data-testid={`${testIdPrefix}-playlists-error-${sid}`}
                >
                  <p className="text-rose-300 mb-1">⚠ {playlistsError}</p>
                  <button
                    type="button"
                    className="text-purple-300 hover:text-purple-200 underline"
                    onClick={retryLoadPlaylists}
                    data-testid={`${testIdPrefix}-playlists-retry-${sid}`}
                  >
                    Retry
                  </button>
                </div>
              ) : playlists.length === 0 ? (
                <Item icon="➕" label="Create a playlist" to="/playlists" testId={`${testIdPrefix}-new-playlist-${sid}`} />
              ) : (
                <div className="max-h-40 overflow-y-auto">
                  {playlists.slice(0, 6).map((p) => (
                    <Item
                      key={p.playlist_id}
                      icon="🎵"
                      label={p.name}
                      onClick={() => handleAddToPlaylist(p.playlist_id)}
                      disabled={!!addingTo}
                      testId={`${testIdPrefix}-add-${p.playlist_id}-${sid}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {tracksShareOn && (
            <div className="border-t border-white/5">
              {/* Share to Community */}
              {currentUser && onShareCommunity && (
                <Item
                  icon="🤝"
                  label="Share to Community"
                  onClick={() => { onShareCommunity(track); setOpen(false); }}
                  testId={`${testIdPrefix}-share-community-${sid}`}
                />
              )}
              {/* Share outside */}
              <Item
                icon="🔗"
                label="Share / Copy link"
                onClick={handleShare}
                testId={`${testIdPrefix}-share-${sid}`}
              />
            </div>
          )}

          {/* Pin / Unpin */}
          {canManage && onPinToggle && (
            <div className="border-t border-white/5">
              <Item
                icon={track?.is_pinned ? "📌" : "📍"}
                label={track?.is_pinned ? "Unpin from top" : "Pin to top"}
                onClick={() => { onPinToggle(track); setOpen(false); }}
                testId={`${testIdPrefix}-pin-${sid}`}
              />
            </div>
          )}

          {/* Delete */}
          {canManage && onDelete && (
            <div className="border-t border-white/5">
              <Item
                icon="🗑"
                label="Delete Track"
                onClick={() => { onDelete(track); setOpen(false); }}
                danger
                testId={`${testIdPrefix}-delete-${sid}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrackActionsMenu;
